import { VerificationHomeQueryResults } from '../useVerificationQuery/types';
import { VerificationStatus } from './types';

/**
 * Count each non-current-user beneficiary by its status.
 */
const countBeneficiariesByStatus = (
  beneficiaries: Required<VerificationHomeQueryResults>['companyLegalBeneficiaries'],
) =>
  beneficiaries.reduce(
    (counts, { isCurrentUser, onboardingStatus }) => {
      if (isCurrentUser) {
        return counts;
      }

      return {
        pending:
          onboardingStatus === 'IN_REVIEW'
            ? counts.pending + 1
            : counts.pending,
        rejected:
          onboardingStatus === 'REFUSED' || onboardingStatus === 'INVALID'
            ? counts.rejected + 1
            : counts.rejected,
        total: counts.total + 1,
        valid:
          onboardingStatus === 'VALIDATED' ? counts.valid + 1 : counts.valid,
      };
    },
    { pending: 0, rejected: 0, total: 0, valid: 0 },
  );

/**
 * Returns data used to display the global status of advancement of the beneficiaries.
 * Includes a global status key `multiKycStatus` and numbers for beneficiaries being completed or invalid.
 */
const getMultiKycStatus = (
  data?: Pick<VerificationHomeQueryResults, 'companyLegalBeneficiaries'>,
): Pick<
  VerificationStatus,
  | 'multiKycStatus'
  | 'totalBeneficiaries'
  | 'totalBeneficiariesCompleted'
  | 'totalBeneficiariesRejected'
> => {
  if (data === undefined || data.companyLegalBeneficiaries === undefined) {
    return { multiKycStatus: 'not-shown' };
  }
  const { companyLegalBeneficiaries } = data;

  const beneficiaries = countBeneficiariesByStatus(companyLegalBeneficiaries);

  if (beneficiaries.total === 0) {
    return { multiKycStatus: 'not-shown' };
  }

  const someHasError = beneficiaries.rejected > 0;
  const allAreValid = beneficiaries.total === beneficiaries.valid;
  const allArePendingOrValid =
    beneficiaries.total === beneficiaries.pending + beneficiaries.valid;

  const multiKycStatus =
    (allAreValid && 'complete') ||
    (allArePendingOrValid && 'pending') ||
    (someHasError && 'error') ||
    'waiting';

  return {
    multiKycStatus,
    totalBeneficiaries: beneficiaries.total,
    totalBeneficiariesCompleted: beneficiaries.valid + beneficiaries.pending,
    totalBeneficiariesRejected: beneficiaries.rejected,
  };
};

export default getMultiKycStatus;
